import View                         from '@widesk-ui/components/View';
import Button                       from '@widesk-ui/components/Button';
import { createPortal }             from 'react-dom';
import { useEffect }                from 'react';
import { useRef }                   from 'react';
import { autorun }                  from 'mobx';
import { observer }                 from 'mobx-react-lite';
import { compassBearing }           from '@tokamap/core';
import { flightHasSlope }           from '@tokamap/core';
import { DEFAULT_STAIRS_SLOPE_DEG } from '@tokamap/core';
import { MAX_STAIRS_SLOPE_DEG }     from '@tokamap/core';
import { MIN_STAIRS_SLOPE_DEG }     from '@tokamap/core';
import { lngLatToMeters }           from '@tokamap/core';
import { DECOR_MODELS_BY_ID }       from '@tokamap/core';
import type { DecorItem }           from '@tokamap/core';
import type { DecorModelBounds }    from '@tokamap/core';
import type { DecorPathEnd }        from '@tokamap/core';
import type { LngLat }              from '@tokamap/core';
import type { TokaMapInstance }     from '@tokamap/core';
import type { WallSnap }            from '@tokamap/core';
import currentEditorStore           from '@/TokamapEditor/stores/currentEditorStore';
import useDesignTokens              from '@/hooks/useDesignTokens';
import Kbd                          from '@/TokamapEditor/ux/Kbd';
import decorStore                   from '@/TokamapEditor/decor/decorStore';
import { openDecorItemSidebar }     from '@/TokamapEditor/decor/decorSidebars';
import { clampDecorScale }          from '@/TokamapEditor/decor/decorGeometry';
import { decorFootprint }           from '@/TokamapEditor/decor/decorGeometry';
import { decorFront }               from '@/TokamapEditor/decor/decorGeometry';
import { translatePath }            from '@/TokamapEditor/decor/decorGeometry';
import { flightBetween }            from '@/TokamapEditor/decor/decorGeometry';
import { reslopedFlight }           from '@/TokamapEditor/decor/decorGeometry';
import { flightFromTop }            from '@/TokamapEditor/decor/decorGeometry';
import { freeFlightModelOf }        from '@/TokamapEditor/decor/decorGeometry';
import type { FlightEnd }           from '@/TokamapEditor/decor/decorGeometry';
import type { FlightPlacement }     from '@/TokamapEditor/decor/decorGeometry';

type Point = { x: number; y: number };

type Drag =
	| { kind: 'move'; id: string; start: LngLat; position: LngLat; path?: LngLat[]; patch?: Partial<DecorItem> }
	| { kind: 'rotate'; id: string; patch?: Partial<DecorItem> }
	| { kind: 'scale'; id: string; startDistance: number; startScale: number; patch?: Partial<DecorItem> };

type Handle = 'rotate' | 'scale';

const HANDLE_SIZE = 10;
const ROTATE_HANDLE_GAP = 26;
const HANDLE_HIT_RADIUS = 9;
const CLICK_TOLERANCE = 5;
const SAME_POINT_TOLERANCE = 6;
const ROTATION_STEP = 15;
const WHEEL_ROTATION_DEG_PER_PIXEL = 0.15;
const WHEEL_LINE_PIXELS = 33;
const WHEEL_NOTCH_PIXELS = 100;
const WHEEL_SCALE_PER_PIXEL = 1.1 ** (1 / WHEEL_NOTCH_PIXELS);
const FLIGHT_SLOPE_STEP_DEG = 1;
const EDIT_COMMIT_DELAY_MS = 400;
const SMALL_ITEM_PX = 28;
const SMALL_ITEM_TARGET_PX = 40;
const ESTIMATED_DIMENSION_RATIO = 0.4;
const SMALL_PREVIEW_HALO_RADIUS = 16;
const SMALL_PREVIEW_BADGE_WIDTH = 56;
const SMALL_PREVIEW_BADGE_HEIGHT = 43;
const SMALL_PREVIEW_BADGE_GAP = 10;
const FLIGHT_LABEL_GAP = 12;
const FLIGHT_LABEL_HEIGHT = 22;

const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

const DecorMapLayer = observer(() => {
	const instance = currentEditorStore.get as TokaMapInstance | undefined;
	const container = instance?.mapAdapter.getContainerElement();

	useEffect(() => instance ? decorStore.bind(instance) : undefined, [instance]);

	if (!instance || !container) return null;

	return createPortal(
		<>
			<DecorOverlay instance={instance} container={container} />
			<DecorHint />
		</>,
		container,
	);
});

export default DecorMapLayer;

function DecorOverlay({ instance, container }: { instance: TokaMapInstance; container: HTMLElement; }) {
	const tokens = useDesignTokens();
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const colors = useRef({ primary: tokens.color.primary, handle: tokens.surface.elevated });

	colors.current = { primary: tokens.color.primary, handle: tokens.surface.elevated };

	useEffect(() => {
		const adapter = instance.mapAdapter;
		const overlay = canvasRef.current;
		const context = overlay?.getContext('2d');
		const mapCanvas = container.querySelector<HTMLCanvasElement>('canvas.maplibregl-canvas');
		if (!overlay || !context || !mapCanvas) return;

		let hover: LngLat | null = null;
		let hoverPoint: Point | null = null;
		let snapDisabled = false;
		let hoverSnap: WallSnap | null = null;
		let hoveredId: string | null = null;
		let down: (Point & { owned: boolean; hitId?: string }) | null = null;
		let drag: Drag | null = null;
		let cursorSet = false;

		const local = (event: MouseEvent): Point => {
			const rect = container.getBoundingClientRect();
			return { x: event.clientX - rect.left, y: event.clientY - rect.top };
		};
		// Tout se dessine et se vise à la hauteur où l'objet se pose, fond des zones creusées compris.
		const toScreen = (position: LngLat, elevation?: number): Point => adapter.projectToScreen(position, elevation ?? adapter.getDecorGroundHeight(position));
		const toLngLat = (point: Point, modelId?: string): LngLat => adapter.screenToDecorGround(point.x, point.y, modelId);
		// Une porte se colle au mur dont on l'approche, sauf touche Alt enfoncée.
		const wallSnapFor = (modelId: string, position: LngLat, event: MouseEvent) => (
			DECOR_MODELS_BY_ID[modelId]?.category === 'door' && !event.altKey ? adapter.snapDecorToWall(position) : null
		);
		const elevationOf = (item: Pick<DecorItem, 'position'> & Partial<Pick<DecorItem, 'elevation' | 'model'>>) => item.elevation ?? adapter.getDecorGroundHeight(item.position, item.model);
		const modelBox = (modelId: string): DecorModelBounds | null => {
			const bounds = adapter.getDecorModelBounds(modelId);
			if (bounds) return bounds;
			const size = DECOR_MODELS_BY_ID[modelId]?.size;
			if (!size) return null;
			const known = Math.max(...Object.values(size));
			const width = 'width' in size ? size.width : ESTIMATED_DIMENSION_RATIO * known;
			const length = 'length' in size ? size.length : ESTIMATED_DIMENSION_RATIO * known;
			const height = 'height' in size ? size.height : ESTIMATED_DIMENSION_RATIO * known;
			return { minX: -width / 2, maxX: width / 2, minY: -length / 2, maxY: length / 2, height };
		};
		const modelScreenSize = (modelId: string, position: LngLat, rotationDeg = decorStore.lastRotationDeg, scale = 1) => {
			const box = modelBox(modelId);
			if (!box) return Infinity;
			const ground = adapter.getDecorGroundHeight(position, modelId);
			const footprint = decorFootprint({ position, rotationDeg, scale }, box);
			const corners = [ground, ground + box.height * scale].flatMap(elevation => footprint.map(corner => toScreen(corner, elevation)));
			const xs = corners.map(corner => corner.x);
			const ys = corners.map(corner => corner.y);
			return Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys));
		};
		const armedFlightModel = () => decorStore.armedModel ? freeFlightModelOf(decorStore.armedModel.id) : null;
		const flightEndAt = (point: Point): FlightEnd => {
			const target = adapter.screenToDecorTarget(point.x, point.y);
			return { position: target.position, ground: target.height };
		};
		// Sans départ, la volée ne se montre que calée contre un bord, où un clic la pose d'emblée.
		const flightAt = (point: Point) => {
			const model = armedFlightModel();
			if (!model) return null;
			const end = flightEndAt(point);
			const start = decorStore.flightStart;
			if (!start) {
				const snap = snapDisabled ? null : adapter.snapDecorFlightTop(end.position);
				return snap ? flightFromTop(model, snap, snap.low, decorStore.flightSlopeDeg ?? undefined) : null;
			}
			const [low, high] = start.ground <= end.ground ? [start, end] : [end, start];
			const snap = snapDisabled ? null : adapter.snapDecorFlightTop(high.position, { toward: low.position });
			return flightBetween(model, start, end, snap, decorStore.flightSlopeDeg ?? undefined);
		};
		const pendingFlight = () => hoverPoint ? flightAt(hoverPoint) : null;
		const thumbnails = new Map<string, HTMLImageElement>();
		const thumbnailOf = (modelId: string) => {
			const url = DECOR_MODELS_BY_ID[modelId]?.thumbnailUrl;
			if (!url) return null;
			let image = thumbnails.get(url);
			if (!image) {
				image = new Image();
				image.onload = () => draw();
				image.src = url;
				thumbnails.set(url, image);
			}
			return image.complete && image.naturalWidth ? image : null;
		};
		const stop = (event: Event) => {
			event.stopPropagation();
			event.preventDefault();
		};

		const handleGeometry = (item: DecorItem) => {
			const bounds = adapter.getDecorItemBounds(item);
			if (!bounds) return null;

			const elevation = elevationOf(item);
			const corners = decorFootprint(item, bounds).map(corner => toScreen(corner, elevation));
			const center = toScreen(item.position, elevation);
			const front = toScreen(decorFront(item, bounds), elevation);
			const angle = Math.atan2(front.y - center.y, front.x - center.x);
			const rotate = { x: front.x + Math.cos(angle) * ROTATE_HANDLE_GAP, y: front.y + Math.sin(angle) * ROTATE_HANDLE_GAP };

			return { corners, front, rotate };
		};

		const hitHandle = (point: Point): Handle | null => {
			const item = decorStore.editing;
			const geometry = item && handleGeometry(item);
			if (!geometry) return null;
			if (distance(point, geometry.rotate) < HANDLE_HIT_RADIUS) return 'rotate';
			if (!freeFlightModelOf(item.model) && geometry.corners.some(corner => distance(point, corner) < HANDLE_HIT_RADIUS)) return 'scale';
			return null;
		};

		const drawPath = (points: LngLat[], end: DecorPathEnd | undefined, tail: LngLat | null) => {
			const { primary, handle } = colors.current;
			const screen = (end === 'loop' ? [...points, points[0]!] : points).map(point => toScreen(point));
			if (tail) screen.push(toScreen(tail));

			context.save();
			context.strokeStyle = primary;
			context.lineWidth = 2.5;
			context.lineCap = 'round';
			context.setLineDash([7, 5]);
			context.beginPath();
			screen.forEach((point, index) => index ? context.lineTo(point.x, point.y) : context.moveTo(point.x, point.y));
			context.stroke();
			context.setLineDash([]);

			for (let index = 0; index < screen.length - 1; index++) {
				const from = screen[index]!;
				const to = screen[index + 1]!;
				context.save();
				context.translate((from.x + to.x) / 2, (from.y + to.y) / 2);
				context.rotate(Math.atan2(to.y - from.y, to.x - from.x));
				context.fillStyle = primary;
				context.beginPath();
				context.moveTo(6, 0);
				context.lineTo(-4, -5);
				context.lineTo(-4, 5);
				context.closePath();
				context.fill();
				context.restore();
			}

			points.map(point => toScreen(point)).forEach((point, index) => {
				context.beginPath();
				context.arc(point.x, point.y, index === 0 ? 6 : 4.5, 0, Math.PI * 2);
				context.fillStyle = index === 0 ? primary : handle;
				context.fill();
				context.lineWidth = 2;
				context.stroke();
			});
			context.restore();
		};

		const drawHandles = (item: DecorItem) => {
			const geometry = handleGeometry(item);
			if (!geometry) return;
			const { primary, handle } = colors.current;
			const { corners, front, rotate } = geometry;

			context.save();
			context.strokeStyle = primary;
			context.lineWidth = 1.5;
			context.beginPath();
			corners.forEach((corner, index) => index ? context.lineTo(corner.x, corner.y) : context.moveTo(corner.x, corner.y));
			context.closePath();
			context.stroke();

			context.beginPath();
			context.moveTo(front.x, front.y);
			context.lineTo(rotate.x, rotate.y);
			context.stroke();
			context.beginPath();
			context.arc(rotate.x, rotate.y, 7, 0, Math.PI * 2);
			context.fillStyle = handle;
			context.fill();
			context.stroke();
			context.beginPath();
			context.arc(rotate.x, rotate.y, 3.2, -Math.PI * 0.1, Math.PI * 1.3);
			context.stroke();

			if (!freeFlightModelOf(item.model)) corners.forEach(corner => {
				context.beginPath();
				context.rect(corner.x - HANDLE_SIZE / 2, corner.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
				context.fillStyle = handle;
				context.fill();
				context.stroke();
			});
			context.restore();
		};

		const drawFootprint = (item: Pick<DecorItem, 'position' | 'rotationDeg' | 'scale' | 'model'> & Partial<DecorItem>, dashed: boolean) => {
			const bounds = item.id ? adapter.getDecorItemBounds(item as DecorItem) : adapter.getDecorModelBounds(item.model);
			if (!bounds) return;

			const elevation = elevationOf(item);
			const corners = decorFootprint(item, bounds).map(corner => toScreen(corner, elevation));
			context.save();
			context.strokeStyle = colors.current.primary;
			context.lineWidth = dashed ? 1.2 : 2;
			if (dashed) context.setLineDash([4, 3]);
			context.beginPath();
			corners.forEach((corner, index) => index ? context.lineTo(corner.x, corner.y) : context.moveTo(corner.x, corner.y));
			context.closePath();
			context.stroke();
			context.restore();
		};

		const drawSmallPreviewMarker = (modelId: string, position: LngLat, rotationDeg: number) => {
			if (modelScreenSize(modelId, position, rotationDeg, decorStore.lastScale) >= SMALL_ITEM_PX) return;
			const { primary, handle } = colors.current;
			const center = toScreen(position, adapter.getDecorGroundHeight(position, modelId));

			context.save();
			context.strokeStyle = primary;
			context.fillStyle = primary;
			context.lineWidth = 2;
			context.globalAlpha = 0.15;
			context.beginPath();
			context.arc(center.x, center.y, SMALL_PREVIEW_HALO_RADIUS, 0, Math.PI * 2);
			context.fill();
			context.globalAlpha = 1;
			context.stroke();

			const image = thumbnailOf(modelId);
			if (image) {
				const left = center.x + SMALL_PREVIEW_BADGE_GAP;
				const top = center.y - SMALL_PREVIEW_HALO_RADIUS - SMALL_PREVIEW_BADGE_GAP - SMALL_PREVIEW_BADGE_HEIGHT;
				context.beginPath();
				context.roundRect(left, top, SMALL_PREVIEW_BADGE_WIDTH, SMALL_PREVIEW_BADGE_HEIGHT, 8);
				context.fillStyle = handle;
				context.fill();
				context.lineWidth = 1.5;
				context.stroke();
				const fit = Math.min((SMALL_PREVIEW_BADGE_WIDTH - 6) / image.naturalWidth, (SMALL_PREVIEW_BADGE_HEIGHT - 6) / image.naturalHeight);
				const width = image.naturalWidth * fit;
				const height = image.naturalHeight * fit;
				context.drawImage(image, left + (SMALL_PREVIEW_BADGE_WIDTH - width) / 2, top + (SMALL_PREVIEW_BADGE_HEIGHT - height) / 2, width, height);
			}
			context.restore();
		};

		const drawFlightGuide = (start: FlightEnd | null, flight: FlightPlacement | null) => {
			if (!hoverPoint) return;
			const { primary, handle } = colors.current;

			context.save();
			context.strokeStyle = primary;
			context.fillStyle = primary;
			context.lineWidth = 2;
			if (start) {
				const from = toScreen(start.position, start.ground);
				context.setLineDash([6, 4]);
				context.beginPath();
				context.moveTo(from.x, from.y);
				context.lineTo(hoverPoint.x, hoverPoint.y);
				context.stroke();
				context.setLineDash([]);
				context.beginPath();
				context.arc(from.x, from.y, 5, 0, Math.PI * 2);
				context.fill();
			}

			if (flight) {
				const base = flight.elevation ?? start?.ground ?? 0;
				const highEnd = toScreen(flight.top, base + flight.depth);
				const lowEnd = toScreen(flight.top, base);
				context.setLineDash([3, 3]);
				context.beginPath();
				context.moveTo(lowEnd.x, lowEnd.y);
				context.lineTo(highEnd.x, highEnd.y);
				context.stroke();
				context.setLineDash([]);
				context.beginPath();
				context.arc(highEnd.x, highEnd.y, 4, 0, Math.PI * 2);
				context.fill();

				const label = `↕ ${flight.depth.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m`;
				context.font = '600 12px system-ui, sans-serif';
				const width = context.measureText(label).width + 16;
				const left = highEnd.x + FLIGHT_LABEL_GAP;
				const top = highEnd.y - FLIGHT_LABEL_HEIGHT / 2;
				context.beginPath();
				context.roundRect(left, top, width, FLIGHT_LABEL_HEIGHT, FLIGHT_LABEL_HEIGHT / 2);
				context.fillStyle = handle;
				context.fill();
				context.lineWidth = 1.5;
				context.stroke();
				context.fillStyle = primary;
				context.textBaseline = 'middle';
				context.fillText(label, left + 8, top + FLIGHT_LABEL_HEIGHT / 2);
			}
			context.restore();
		};

		const draw = () => {
			const ratio = window.devicePixelRatio || 1;
			const width = container.clientWidth;
			const height = container.clientHeight;
			if (overlay.width !== Math.round(width * ratio) || overlay.height !== Math.round(height * ratio)) {
				overlay.width = Math.round(width * ratio);
				overlay.height = Math.round(height * ratio);
			}
			context.setTransform(ratio, 0, 0, ratio, 0, 0);
			context.clearRect(0, 0, width, height);

			const item = decorStore.selected;
			const draft = decorStore.pathDraft;
			void decorStore.loadedModels;

			if (draft) drawPath(draft.points, 'restart', hover);
			else if (item?.path) drawPath(item.path, item.pathEnd, null);
			if (item && !draft && decorStore.editing) drawHandles(item);
			else if (item && !draft) drawFootprint(item, false);
			const flight = decorStore.armedModel && hover ? pendingFlight() : null;
			if (decorStore.armedModel && hover && (decorStore.flightStart || flight)) {
				drawFlightGuide(decorStore.flightStart, flight);
			} else if (decorStore.armedModel && hover) {
				const position = hoverSnap?.position ?? hover;
				drawFootprint({ model: decorStore.armedModel.id, position, rotationDeg: hoverSnap?.rotationDeg ?? decorStore.lastRotationDeg, scale: decorStore.lastScale }, true);
				drawSmallPreviewMarker(decorStore.armedModel.id, position, hoverSnap?.rotationDeg ?? decorStore.lastRotationDeg);
			}
			adapter.setDecorHighlight({ selectedId: decorStore.selectedId, hoveredId });
		};

		const zoomOnSmallItem = (item: DecorItem) => {
			const size = modelScreenSize(item.model, item.position, item.rotationDeg) * item.scale;
			if (size >= SMALL_ITEM_PX) return;

			adapter.zoomBy(Math.log2(SMALL_ITEM_TARGET_PX / Math.max(size, 1)), item.position);
		};

		const placeFlightEnd = (point: Point) => {
			if (!armedFlightModel()) return;
			const flight = flightAt(point);
			if (flight) {
				decorStore.place(flight.position, flight.rotationDeg, { depth: flight.depth, elevation: flight.elevation, scale: 1, slopeDeg: flight.slopeDeg });
				decorStore.cancelFlight();
			} else if (!decorStore.flightStart) {
				decorStore.startFlight(flightEndAt(point));
			}
			updatePreview();
			draw();
		};

		const updatePreview = () => {
			const model = decorStore.armedModel;
			const flight = model && armedFlightModel() ? pendingFlight() : null;
			if (model && (flight || decorStore.flightStart)) {
				adapter.setDecorPreview(flight
					? { id: 'preview', model: model.id, position: flight.position, rotationDeg: flight.rotationDeg, scale: 1, depth: flight.depth, ...(flight.elevation !== undefined ? { elevation: flight.elevation } : {}), ...(flight.slopeDeg !== undefined ? { slopeDeg: flight.slopeDeg } : {}) }
					: null);
				return;
			}
			const flightModel = armedFlightModel();
			adapter.setDecorPreview(model && hover
				? flightModel
					? { id: 'preview', model: model.id, position: hover, rotationDeg: decorStore.lastRotationDeg, scale: 1, ...(decorStore.flightSlopeDeg !== null ? { slopeDeg: decorStore.flightSlopeDeg } : {}) }
					: { id: 'preview', model: model.id, position: hoverSnap?.position ?? hover, rotationDeg: hoverSnap?.rotationDeg ?? decorStore.lastRotationDeg, scale: decorStore.lastScale }
				: null);
		};

		// Le moteur réécrit le curseur de la carte à chaque mouvement : celui du décor passe par une règle
		// prioritaire, posée sur le conteneur.
		ensureDecorCursorStyle();
		const setCursor = (cursor: string | null) => {
			if (cursor) {
				container.dataset.decorCursor = cursor;
				cursorSet = true;
			} else if (cursorSet) {
				delete container.dataset.decorCursor;
				cursorSet = false;
			}
		};

		const onDragMove = (event: MouseEvent) => {
			if (!drag) return;
			const item = decorStore.items.find(candidate => candidate.id === drag!.id);
			if (!item) return;
			const pointer = toLngLat(local(event), item.model);

			if (drag.kind === 'move') {
				const delta: LngLat = [pointer[0] - drag.start[0], pointer[1] - drag.start[1]];
				const position: LngLat = [drag.position[0] + delta[0], drag.position[1] + delta[1]];
				const snap = wallSnapFor(item.model, position, event);
				drag.patch = snap
					? { position: snap.position, rotationDeg: snap.rotationDeg, path: translatePath(drag.path, delta) }
					: { position, path: translatePath(drag.path, delta) };
			} else if (drag.kind === 'rotate') {
				const bearing = compassBearing(item.position, pointer);
				drag.patch = { rotationDeg: event.shiftKey ? Math.round(bearing / ROTATION_STEP) * ROTATION_STEP : bearing };
			} else {
				const { east, north } = lngLatToMeters(item.position, pointer);
				drag.patch = { scale: clampDecorScale(drag.startScale * Math.hypot(east, north) / drag.startDistance) };
			}

			decorStore.update(drag.id, drag.patch, true);
		};

		const onDragEnd = () => {
			window.removeEventListener('mousemove', onDragMove, true);
			window.removeEventListener('mouseup', onDragEnd, true);
			if (drag?.patch) decorStore.update(drag.id, drag.patch);
			drag = null;
		};

		// Sur window et en capture : le mouseup d'un geste qui nous appartient est arrêté sur le conteneur.
		const startDrag = (next: Drag) => {
			drag = next;
			window.addEventListener('mousemove', onDragMove, true);
			window.addEventListener('mouseup', onDragEnd, true);
		};

		const onMouseDown = (event: MouseEvent) => {
			if (event.button !== 0 || event.target !== mapCanvas) return;
			const point = local(event);
			down = { ...point, owned: false };
			if (decorStore.busy) return;

			const item = decorStore.editing;
			const handle = hitHandle(point);
			if (item && handle === 'rotate') {
				down.owned = true;
				stop(event);
				startDrag({ kind: 'rotate', id: item.id });
				return;
			}
			if (item && handle === 'scale') {
				const { east, north } = lngLatToMeters(item.position, toLngLat(point));
				down.owned = true;
				stop(event);
				startDrag({ kind: 'scale', id: item.id, startDistance: Math.max(0.1, Math.hypot(east, north)), startScale: item.scale });
				return;
			}

			const hitId = adapter.pickDecor(point);
			const hit = hitId ? decorStore.items.find(candidate => candidate.id === hitId) : null;
			if (hit && decorStore.editingId === hit.id) {
				down.owned = true;
				stop(event);
				adapter.clearSelection();
				startDrag({ kind: 'move', id: hit.id, start: toLngLat(point, hit.model), position: hit.position, path: hit.path });
				return;
			}
			// Hors mode modification, appuyer sur un élément laisse la carte se déplacer : seul un clic sans
			// mouvement ouvre sa fiche.
			if (hit) down.hitId = hit.id;
		};

		const stopOwnedMouseUp = (event: MouseEvent) => {
			if (down?.owned && event.target === mapCanvas) stop(event);
		};

		const onClick = (event: MouseEvent) => {
			if (event.target !== mapCanvas || !down) return;
			const point = local(event);
			if (down.owned) return stop(event);
			if (distance(point, down) > CLICK_TOLERANCE) return;

			if (down.hitId) {
				stop(event);
				adapter.clearSelection();
				openDecorItemSidebar(down.hitId);
				return;
			}

			const draft = decorStore.pathDraft;
			if (draft) {
				stop(event);
				const last = draft.points[draft.points.length - 1];
				if (!last || distance(toScreen(last), point) > SAME_POINT_TOLERANCE) decorStore.addPathPoint(toLngLat(point));
				return;
			}

			if (decorStore.armedModel) {
				stop(event);
				const position = toLngLat(point, decorStore.armedModel.id);
				if (armedFlightModel()) return placeFlightEnd(point);
				const snap = wallSnapFor(decorStore.armedModel.id, position, event);
				const placed = decorStore.place(snap?.position ?? position, snap?.rotationDeg);
				if (placed) zoomOnSmallItem(placed);
				return;
			}

			if (decorStore.selectedId) decorStore.select(null);
			decorStore.dismissPathOffer();
		};

		const onDoubleClick = (event: MouseEvent) => {
			if (event.target !== mapCanvas) return;
			if (decorStore.pathDraft) {
				stop(event);
				decorStore.finishPath();
				return;
			}
			const hitId = adapter.pickDecor(local(event));
			if (hitId) {
				stop(event);
				openDecorItemSidebar(hitId);
				decorStore.edit(hitId);
			}
		};

		const onMouseMove = (event: MouseEvent) => {
			if (event.target !== mapCanvas) return;
			const point = local(event);
			hover = toLngLat(point, decorStore.armedModel?.id);
			hoverPoint = point;
			snapDisabled = event.altKey;
			hoverSnap = decorStore.armedModel ? wallSnapFor(decorStore.armedModel.id, hover, event) : null;

			if (decorStore.busy) {
				hoveredId = null;
				setCursor('crosshair');
				if (decorStore.armedModel) updatePreview();
				draw();
				return;
			}
			if (drag) return;

			const handle = hitHandle(point);
			const hit = adapter.pickDecor(point);
			if (hit !== hoveredId) {
				hoveredId = hit;
				draw();
			}
			setCursor(handle === 'rotate' ? 'grab' : handle === 'scale' ? 'nwse-resize' : hoveredId ? (hoveredId === decorStore.editingId ? 'move' : 'pointer') : null);
		};

		const onMouseLeave = () => {
			hover = null;
			hoverPoint = null;
			hoverSnap = null;
			hoveredId = null;
			if (decorStore.armedModel) updatePreview();
			draw();
		};

		const onKeyDown = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement | null;
			if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
			if (event.ctrlKey || event.metaKey || event.altKey) return;

			const item = decorStore.selected;

			if (event.key === 'Escape') {
				if (decorStore.pathDraft) decorStore.cancelPath();
				else if (decorStore.flightStart) decorStore.cancelFlight();
				else if (decorStore.armedModel) decorStore.arm(null);
				else if (decorStore.pathOfferId) decorStore.dismissPathOffer();
				else if (decorStore.editing) decorStore.edit(null);
				else if (item) decorStore.select(null);
				else return;
				return stop(event);
			}

			if (event.key.toLowerCase() === 't' && decorStore.pathOffer && !decorStore.pathDraft) {
				decorStore.startPath(decorStore.pathOffer.id);
				return stop(event);
			}

			if (event.key === 'Enter' && decorStore.pathDraft) {
				decorStore.finishPath();
				return stop(event);
			}

			if ((event.key === 'Delete' || event.key === 'Backspace') && item && !decorStore.pathDraft) {
				decorStore.remove(item.id);
				return stop(event);
			}
		};

		// Sur window et en capture : le relâchement d'un glisser parti du catalogue doit être vu avant que
		// celui-ci ne le clôture.
		const onDragRelease = (event: MouseEvent) => {
			if (!decorStore.dragging || !decorStore.armedModel || event.target !== mapCanvas) return;
			const point = local(event);
			if (armedFlightModel()) {
				decorStore.endDrag(true);
				return placeFlightEnd(point);
			}
			const position = toLngLat(point, decorStore.armedModel.id);
			const snap = wallSnapFor(decorStore.armedModel.id, position, event);
			const placed = decorStore.place(snap?.position ?? position, snap?.rotationDeg);
			decorStore.endDrag();
			if (placed) zoomOnSmallItem(placed);
		};

		let editCommit: { id: string; patch: Partial<DecorItem>; timer: ReturnType<typeof setTimeout> } | null = null;
		const flushEditCommit = () => {
			if (!editCommit) return;
			clearTimeout(editCommit.timer);
			decorStore.update(editCommit.id, editCommit.patch);
			editCommit = null;
		};
		// Chaque cran s'applique aussitôt, mais l'enregistrement et l'historique attendent la fin du geste.
		const updateEditedLater = (item: DecorItem, patch: Partial<DecorItem>) => {
			if (editCommit && editCommit.id !== item.id) flushEditCommit();
			decorStore.update(item.id, patch, true);
			const merged = { ...(editCommit?.patch ?? {}), ...patch };
			if (editCommit) clearTimeout(editCommit.timer);
			editCommit = { id: item.id, patch: merged, timer: setTimeout(flushEditCommit, EDIT_COMMIT_DELAY_MS) };
		};
		const wheelEdited = (item: DecorItem, pixels: number, alt: boolean) => {
			const flightModel = freeFlightModelOf(item.model);
			if (flightModel) {
				if (alt || !flightHasSlope(flightModel)) return;
				const current = Math.round(item.slopeDeg ?? DEFAULT_STAIRS_SLOPE_DEG);
				const slope = Math.min(MAX_STAIRS_SLOPE_DEG, Math.max(MIN_STAIRS_SLOPE_DEG, Math.round(current - pixels / WHEEL_NOTCH_PIXELS * FLIGHT_SLOPE_STEP_DEG)));
				if (slope !== current) updateEditedLater(item, reslopedFlight(flightModel, item, slope));
				return;
			}
			if (alt) updateEditedLater(item, { scale: clampDecorScale(item.scale * WHEEL_SCALE_PER_PIXEL ** -pixels) });
			else updateEditedLater(item, { rotationDeg: item.rotationDeg + pixels * WHEEL_ROTATION_DEG_PER_PIXEL });
		};

		const onWheel = (event: WheelEvent) => {
			if (!(event.shiftKey || event.altKey)) return;
			const edited = !decorStore.armedModel && !decorStore.pathDraft ? decorStore.editing : null;
			if (!decorStore.armedModel && !edited) return;
			stop(event);
			const pixels = (event.deltaY || event.deltaX) * (event.deltaMode === WheelEvent.DOM_DELTA_LINE ? WHEEL_LINE_PIXELS : 1);
			if (edited) return wheelEdited(edited, pixels, event.altKey);
			const flightModel = armedFlightModel();
			if (flightModel) {
				if (!event.shiftKey) return;
				if (flightHasSlope(flightModel)) decorStore.adjustFlightSlope(-pixels / WHEEL_NOTCH_PIXELS * FLIGHT_SLOPE_STEP_DEG);
				updatePreview();
				draw();
				return;
			}
			if (event.altKey) decorStore.scaleArmed(WHEEL_SCALE_PER_PIXEL ** -pixels);
			else decorStore.rotateArmed(pixels * WHEEL_ROTATION_DEG_PER_PIXEL);
			updatePreview();
			draw();
		};

		// En capture : un geste commencé sur un élément de décor ne doit jamais atteindre l'éditeur, qui y
		// verrait une sélection de forme ou un déplacement de carte.
		container.addEventListener('mousedown', onMouseDown, true);
		container.addEventListener('mouseup', stopOwnedMouseUp, true);
		container.addEventListener('click', onClick, true);
		container.addEventListener('dblclick', onDoubleClick, true);
		container.addEventListener('mousemove', onMouseMove, true);
		container.addEventListener('wheel', onWheel, { capture: true, passive: false });
		window.addEventListener('mouseup', onDragRelease, true);
		mapCanvas.addEventListener('mouseleave', onMouseLeave);
		window.addEventListener('keydown', onKeyDown, true);

		const stopShapeClick = adapter.on(event => {
			if (event.type === 'shape:click' && !decorStore.busy) decorStore.select(null);
		});
		const stopCameraMove = adapter.onCameraMove(draw);
		const stopDrawing = autorun(draw);

		return () => {
			container.removeEventListener('mousedown', onMouseDown, true);
			container.removeEventListener('mouseup', stopOwnedMouseUp, true);
			container.removeEventListener('click', onClick, true);
			container.removeEventListener('dblclick', onDoubleClick, true);
			container.removeEventListener('mousemove', onMouseMove, true);
			container.removeEventListener('wheel', onWheel, true);
			window.removeEventListener('mouseup', onDragRelease, true);
			mapCanvas.removeEventListener('mouseleave', onMouseLeave);
			window.removeEventListener('keydown', onKeyDown, true);
			window.removeEventListener('mousemove', onDragMove, true);
			window.removeEventListener('mouseup', onDragEnd, true);
			flushEditCommit();
			stopShapeClick();
			stopCameraMove();
			stopDrawing();
			setCursor(null);
			adapter.setDecorPreview(null);
			adapter.setDecorHighlight({ selectedId: null, hoveredId: null });
		};
	}, [instance, container]);

	return (
		<canvas
			ref={canvasRef}
			style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2 }}
		/>
	);
}

const DecorHint = observer(() => {
	const tokens = useDesignTokens();
	const draft = decorStore.pathDraft;
	const model = decorStore.armedModel;
	const offer = decorStore.pathOffer;
	const flightModel = model ? freeFlightModelOf(model.id) : null;
	const offerButton = offer && <Button type={model ? undefined : 'primary'} onClick={() => decorStore.startPath(offer.id)}>Tracer son trajet</Button>;
	const editing = decorStore.editing;
	const editingFlight = editing ? freeFlightModelOf(editing.model) : null;

	if (!draft && !model && !offer && !editing) return null;

	return (
		<View abs absB={16} absL={0} w="100%" flex center zIndex={10} style={{ pointerEvents: 'none', userSelect: 'none' }}>
			<View
				row
				centerV
				gap="md"
				pl={16}
				pr={8}
				py={8}
				br={999}
				bg={tokens.surface.elevated}
				size={13}
				style={{ boxShadow: tokens.shadow.elevated, pointerEvents: 'auto' }}
			>
				{draft ? (
					<>
						<View bold>Trajet · {draft.points.length} point{draft.points.length > 1 ? 's' : ''}</View>

						<View row centerV gap="sm" size={12} color={tokens.text.secondary}>
							<View row centerV gap="xs"><Kbd>Clic</Kbd>ajouter un point</View>
							<View row centerV gap="xs"><Kbd>Double-clic</Kbd>terminer</View>
						</View>

						<Button onClick={() => decorStore.cancelPath()}>Annuler</Button>
						<Button type="primary" disabled={draft.points.length < 2} onClick={() => decorStore.finishPath()}>Terminer</Button>
					</>
				) : model ? (
					<>
						<View bold>{DECOR_MODELS_BY_ID[model!.id]!.label}</View>

						<View row centerV gap="sm" size={12} color={tokens.text.secondary}>
							{flightModel ? (
								<>
									<View row centerV gap="xs"><Kbd>Clic</Kbd>{decorStore.flightStart ? 'arrivée' : 'départ, ou poser contre un bord'}</View>
									{flightHasSlope(flightModel) && <View row centerV gap="xs"><Kbd>Maj + molette</Kbd>pente {Math.round(decorStore.flightSlopeDeg ?? DEFAULT_STAIRS_SLOPE_DEG)}°</View>}
									<View row centerV gap="xs"><Kbd>Alt</Kbd>sans magnétisme</View>
								</>
							) : (
								<>
									<View row centerV gap="xs"><Kbd>Clic</Kbd>poser</View>
									<View row centerV gap="xs"><Kbd>Maj + molette</Kbd>pivoter</View>
									<View row centerV gap="xs"><Kbd>Alt + molette</Kbd>taille ×{(Math.round(decorStore.lastScale * 100) / 100).toLocaleString('fr-FR')}</View>
								</>
							)}
							{offer && <View row centerV gap="xs"><Kbd>T</Kbd>tracer son trajet</View>}
							<View row centerV gap="xs"><Kbd>Échap</Kbd>terminer</View>
						</View>

						{offerButton}
						<Button onClick={() => decorStore.arm(null)}>Terminer</Button>
					</>
				) : editing && !offer ? (
					<>
						<View bold>{DECOR_MODELS_BY_ID[editing.model]?.label}</View>

						<View row centerV gap="sm" size={12} color={tokens.text.secondary}>
							<View row centerV gap="xs"><Kbd>Glisser</Kbd>déplacer</View>
							{editingFlight ? (
								flightHasSlope(editingFlight) && <View row centerV gap="xs"><Kbd>Maj + molette</Kbd>pente {Math.round(editing.slopeDeg ?? DEFAULT_STAIRS_SLOPE_DEG)}°</View>
							) : (
								<>
									<View row centerV gap="xs"><Kbd>Maj + molette</Kbd>pivoter</View>
									<View row centerV gap="xs"><Kbd>Alt + molette</Kbd>taille ×{(Math.round(editing.scale * 100) / 100).toLocaleString('fr-FR')}</View>
								</>
							)}
							<View row centerV gap="xs"><Kbd>Suppr</Kbd>supprimer</View>
							<View row centerV gap="xs"><Kbd>Échap</Kbd>terminer</View>
						</View>

						<Button onClick={() => decorStore.edit(null)}>Terminer</Button>
					</>
				) : (
					<>
						<View bold>{DECOR_MODELS_BY_ID[offer!.model]!.label}</View>

						<View size={12} color={tokens.text.secondary}>Lui faire suivre un trajet ?</View>

						<View row centerV gap="sm" size={12} color={tokens.text.secondary}>
							<View row centerV gap="xs"><Kbd>T</Kbd>tracer</View>
							<View row centerV gap="xs"><Kbd>Échap</Kbd>plus tard</View>
						</View>

						<Button onClick={() => decorStore.dismissPathOffer()}>Plus tard</Button>
						{offerButton}
					</>
				)}
			</View>
		</View>
	);
});

const DECOR_CURSORS = ['pointer', 'move', 'grab', 'nwse-resize', 'crosshair'];

function ensureDecorCursorStyle() {
	if (document.getElementById('tokamap-decor-cursor')) return;
	const style = document.createElement('style');
	style.id = 'tokamap-decor-cursor';
	style.textContent = DECOR_CURSORS
		.map(cursor => `[data-decor-cursor="${cursor}"] canvas.maplibregl-canvas { cursor: ${cursor} !important; }`)
		.join('\n');
	document.head.appendChild(style);
}
